import React, { memo } from "react";
import { useRecoilValue } from "recoil";
import { lineProfileState } from "../../../store/lineProfileState";
import { Box, Button, useDisclosure } from '@chakra-ui/react'
import { ConfirmCouponUseDialog } from "./ConfirmCouponUseDialog";
import { useChangeSegmentCouponState } from "../../../hooks/membership/useChangeSegmentCouponState";
// ... existing code ...
type ConfirmSegmentCouponUseDialogProps = {
  couponId: string;
  isUsed: boolean;
  onUsed?: () => void;         // 押下直後の楽観更新
  onUsedRevert?: () => void;   // 失敗時のロールバック
}

// 循環参照対応 stringify（デバッグ用）
const safeStringify = (value: any) => {
  const cache = new Set<any>();
  const s = JSON.stringify(value, (_k, v) => {
    if (typeof v === 'object' && v !== null) {
      if (cache.has(v)) return '[Circular]';
      cache.add(v);
    }
    if (v instanceof Error) return { name: v.name, message: v.message, stack: v.stack };
    return v;
  }, 2);
  cache.clear();
  return s;
};

// デバッグ有効判定（ENV=false なら常に無効）
const isDebugEnabledStrict = (): boolean => {
  // 1) ENV が true のときだけデバッグ許可
  if (process.env.REACT_APP_MANLABO_DEBUG !== 'true') return false;

  // 2) 明示OFFを最優先
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w: any = window as any;
    if (w && w.__MANLABO_DEBUG__ === false) return false;
  } catch {}
  try {
    if (localStorage.getItem('MANLABO_DEBUG') === '0') return false;
  } catch {}

  // 3) ここまで来たらデバッグON（ENV=true前提）
  return true;
};

export const ConfirmSegmentCouponUseDialog: React.FC<ConfirmSegmentCouponUseDialogProps> = memo(({couponId, isUsed, onUsed, onUsedRevert}) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const lineProfile = useRecoilValue(lineProfileState)
  const lineId = lineProfile!.userId
  const { isLoading, handleOnClick } = useChangeSegmentCouponState({couponId, lineId, onClose})

  // 毎レンダーで厳密評価
  const debug = isDebugEnabledStrict();

  const onOpenWithDebug = React.useCallback(() => {
    if (debug) {
      const payload = {
        event: 'onOpen',
        couponId,
        lineId,
        isUsedPropBefore: isUsed,
        at: new Date().toISOString(),
      };
      // 先にconsole出力（alertがブロックされても痕跡が残る）
      // eslint-disable-next-line no-console
      console.log('[ConfirmSegmentCouponUseDialog]', payload);
      try { window.alert(safeStringify(payload)); } catch {}
    }
    onOpen();
  }, [debug, couponId, lineId, isUsed, onOpen]);

  // 確認ダイアログの「はい」→使用確定
  const onConfirmWithDebug = React.useCallback(async () => {
    if (debug) {
      const start = { event: 'onConfirm:start', couponId, lineId, isUsedPropBefore: isUsed, at: new Date().toISOString() };
      // eslint-disable-next-line no-console
      console.log('[ConfirmSegmentCouponUseDialog]', start);
      try { window.alert(safeStringify(start)); } catch {}
    }

    // 楽観更新
    onUsed && onUsed();

    try {
      const res = await Promise.resolve(handleOnClick());
      if (debug) {
        const ok = { event: 'onConfirm:success', response: res, at: new Date().toISOString() };
        // eslint-disable-next-line no-console
        console.log('[ConfirmSegmentCouponUseDialog]', ok);
        try { window.alert(safeStringify(ok)); } catch {}
      }
      return res;
    } catch (e: any) {
      // ロールバック
      onUsedRevert && onUsedRevert();
      if (debug) {
        const ng = {
          event: 'onConfirm:error',
          error: e,
          error_details: {
            name: e?.name, message: e?.message, stack: e?.stack,
            response: e?.response, status: e?.response?.status, data: e?.response?.data
          },
          at: new Date().toISOString(),
        };
        // eslint-disable-next-line no-console
        console.error('[ConfirmSegmentCouponUseDialog]', ng);
        try { window.alert(safeStringify(ng)); } catch {}
      }
      throw e;
    }
  }, [debug, couponId, lineId, isUsed, handleOnClick, onUsed, onUsedRevert]);

  return (
    <Box textAlign='center' position='relative'>
      <Button variant='solid' colorScheme="blue" w='70%' onClick={onOpenWithDebug} isDisabled={isUsed}>
        {isUsed ? '使用済み' : '使用する'}
      </Button>
      <ConfirmCouponUseDialog
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={onConfirmWithDebug}
      />
      {!debug && (
        <Button
          size='xs'
          position='fixed'
          bottom='10px'
          right='10px'
          zIndex={1000}
          colorScheme='pink'
          variant='outline'
          isDisabled
          title="Debug mode is ON"
        >
          DBG
        </Button>
      )}
    </Box>
    );
});

